"use client";
import TextField from "../ui/textField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { phoneNumberSchema } from "@/validations";
import { buildHrefWithRawQuery, cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { useRouter, useSearchParams } from "next/navigation";
import { TSendOtpForm, TSendOtpPayload } from "@/interfaces";
import { useSendOtpMutation } from "@/hooks";
import ErrorMessage from "../ui/errorMessage";
import { useAppStore } from "@/store/general";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { FlexBetween } from "../ui";
import { useModalStore } from "@/store";
import RulesDialog from "../rulesDialog";

const SendOtpForm = ({
  nextFormStep,
  fromForgotPass,
  btnLabel,
  isPage = false,
  backFormStep,
}: {
  btnLabel: string;
  fromForgotPass?: boolean;
  isPage?: boolean;
  backFormStep?: () => void;
  nextFormStep?: () => void;
}) => {
  const t = useTranslations("auth");
  const searchParams = useSearchParams();
  const storePhoneNumber = useAppStore((state) => state.setPhoneNumber);
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const router = useRouter();
  const {
    isPending: isSending,
    mutate: sendOtpFn,
    error: sendOtpError,
  } = useSendOtpMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TSendOtpForm>({
    resolver: zodResolver(phoneNumberSchema),
  });

  const onSubmit = (data: TSendOtpForm) => {
    const payload: Readonly<TSendOtpPayload> = {
      mobile: data.phoneNumber,
    };
    sendOtpFn(payload, {
      onSuccess: () => {
        storePhoneNumber(data.phoneNumber);

        if (isPage) {
          const redirectPath = searchParams?.toString();
          const targetPath = fromForgotPass
            ? routes.auth.FORGOT_PASSWORD_VERIFY.primaryPath
            : routes.auth.SEND_OTP_VERIFY.primaryPath;

          router.push(
            searchParams
              ? buildHrefWithRawQuery(targetPath, searchParams)
              : targetPath,
          );
          return;
        }
        nextFormStep?.();
      },
    });
  };
  const handleBackStep = () => {
    if (isPage) {
      router.back();
      return;
    }
    if (backFormStep) {
      backFormStep();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={cn("mb-4 w-full")}>
        <fieldset>
          <TextField
            hasShadow
            {...register("phoneNumber")}
            placeholder={t("placeholders.phoneNumber")}
            numeric
            inputMode="numeric"
            aria-label="phone number"
            className="mb-4"
            autoFocus
            maxLength={11}
            inputClassName="h-10 "
            value={watch("phoneNumber")}
            error={errors.phoneNumber?.message}
          />
        </fieldset>
        {sendOtpError && <ErrorMessage message={sendOtpError.message} />}
        <FlexBetween gap={4} className="mx-auto w-max">
          {fromForgotPass && (
            <CustomButton
              id="send-otp-back-step"
              variant={"gray"}
              className="mt-8 w-max px-8!"
              onClick={handleBackStep}
              disabled={isSending}
            >
              بازگشت
            </CustomButton>
          )}
          <CustomButton
            id="send-otp-submit"
            type="submit"
            className="mt-5 w-max md:mt-8"
            isLoading={isSending}
          >
            {btnLabel}
          </CustomButton>
        </FlexBetween>
        <p className="mt-6 text-center text-xs text-gray-500 md:text-sm">
          با ثبت نام در این سامانه با &nbsp;
          <span
            onClick={() => setOpenModal("terms-and-conditions")}
            className="text-primary-main cursor-pointer font-bold"
          >
            قوانین و مقررات
          </span>
          &nbsp; موافقت می نمایم.
        </p>
      </form>
      {openModal === "terms-and-conditions" && <RulesDialog />}
    </>
  );
};

export default SendOtpForm;
